/**
 * The pre-close lock REQUEST form, on the panel-form standard
 * (FORM_STYLE_GUIDE.md §12b): sections declared as data, label-above inputs,
 * the shared primitives, and a pinned `FormPanelFooter`.
 *
 * One form, three jobs — all three post the same price-input shape:
 *   "request"  no active lock → request_dscr_lock / request_rtl_lock
 *   "update"   a requester resubmits a Requested lock (supersedes it)
 *   "edit"     the desk amends a Requested lock in place (update_lock_details)
 *
 * A SIZER is required to request (and resubmit) a lock: upload one, or pick a
 * document already on the loan. update_lock_details is a JSON whitelist that
 * carries no sizer, so "edit" has no Sizer section and posts only what changed.
 *
 * Shell: the host decides where the body and footer go through `frame`. The
 * Loan Lock sidebar puts them in a panel pane (footer pinned); with no frame
 * (the Lock Queue's expanded row) they stack inline.
 *
 * Backend contract, unchanged from the hand-rolled form this replaced:
 *   • percent fields are DECIMAL fractions (FormPercentField stores 0.07125)
 *   • dates are ISO UTC-midnight strings (FormDateField)
 *   • a saved DSCR workbook supplies calculated_dscr_at_lock (the server
 *     overwrites it with verifiedDscr anyway) and a below-floor DSCR blocks
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import isNil from "lodash/isNil";
import size from "lodash/size";

import { dscrCalcApi } from "../../../../api/dscrCalcApi";
import {
  LOCK_CHANNEL_OPTIONS,
  RTL_BORROWER_CLASSIFICATIONS,
  defaultLockChannelForDeal,
  lockChannelOf,
} from "../../../../api/losLocksApi";
import Flx from "../../../../components/layout/Flx";
import {
  FormFileDrop,
  FormInfo,
  FormNote,
  FormPanelFooter,
  FormPill,
  FormSelectField,
  FormTextField,
  FormWarn,
} from "../../../../components/forms/FormPrimitives";
import {
  PanelSections,
  countRequiredFilled,
  isFilled,
  requiredOf,
} from "../../../../components/forms/panelFormModel";
import { loanFetchedDataDscrCalcSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { FORM, formSx, px } from "../../../../theme/formStyles";
import { getLockProduct } from "./loanLockUtils";

/* ───────────────────────────── constants ───────────────────────────────── */

// Requesters can only ask for 15 or 30 days (default 30); anything longer needs
// a pricing exception. The desk still sets the final expiration at confirmation.
const REQUEST_LOCK_PERIODS = [15, 30];
const DEFAULT_REQUEST_LOCK_PERIOD = 30;

const SIZER_DOC_GROUP = "Terms & Sizer";
const SIZER_DOC_TYPE = "Sizer";
const isSizerDoc = (d) =>
  d?.docGroup === SIZER_DOC_GROUP && d?.docType === SIZER_DOC_TYPE;
const docRefOf = (d) => d?._id || d?.file_storage_name || null;
const docLabelOf = (d) =>
  d?.file_display_name || d?.fileName || d?.docType || "Document";

const PRODUCT_OPTIONS = [
  { value: "dscr", label: "DSCR (bond price)" },
  { value: "rtl", label: "RTL (buy rate + spread)" },
];

const HINTS = {
  request:
    "This is a preliminary request. The capital-markets desk confirms it, sets the counterparty, and finalizes the expiration.",
  update:
    "Update the price inputs and resubmit. This replaces the current request and notifies the lock desk.",
  edit: "Amend the requested price terms in place. This updates the existing lock without resetting confirmation.",
};

const SUBMIT_TEXT = {
  request: "Send request to desk",
  update: "Resubmit request",
  edit: "Save changes",
};

// A choice the form must always hold — clicking the selected chip doesn't
// clear these.
const NEVER_EMPTY = ["product", "lock_period_days", "lock_channel"];

const fmtDscrX = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}x`;

/* ─────────────────────── DSCR workbook gate ────────────────────────────── */
// dscr-calculator-workbook.md §Verified-DSCR gating (decided 2026-08-25): once
// the deal has a SAVED workbook, the verified DSCR is authoritative at lock and
// the server 400s a request below the program floor. This shows that BEFORE
// the request goes out. No saved workbook → the typed DSCR stands.

/**
 * "Saved" mirrors the server's gate condition (`stored.sources` non-empty). The
 * GET seeds rows from the units when nothing is stored, so a populated
 * `sources[]` alone isn't proof of a save — `initialEntry` is written on the
 * first save with sources, `changeHistory` on every changing save.
 */
const workbookGoverns = (calc) =>
  size(calc?.sources) > 0 &&
  (!isNil(calc?.initialEntry) ||
    size(calc?.changeHistory) > 0 ||
    !!calc?.updatedBy);

const useDscrWorkbook = (dealId, enabled) => {
  const dispatch = useDispatch();
  const calc = useSelector((state) => state.loanFetchedData?.dscrCalc);
  useEffect(() => {
    if (!enabled || !dealId || !isNil(calc)) return;
    dscrCalcApi
      .get(dealId)
      .then((data) => dispatch(loanFetchedDataDscrCalcSet(data || {})))
      .catch(() => dispatch(loanFetchedDataDscrCalcSet({})));
  }, [enabled, dealId, calc, dispatch]);
  return calc;
};

const DscrWorkbookGate = ({ calc }) => {
  const verifiedCount = (calc?.sources || []).filter((s) => s?.verified).length;
  return (
    <Flx column sx={{ gap: px(FORM.s.s3) }}>
      <FormTextField
        label="Calculated DSCR"
        value={fmtDscrX(calc?.verifiedDscr)}
        onChange={() => {}}
        disabled
        helperText="Set by the DSCR workbook (verified rows only). The desk records this number, not a typed one."
      />
      <Flx sx={{ flexWrap: "wrap", gap: px(FORM.s.s2) }}>
        <FormPill>Stated {fmtDscrX(calc?.statedDscr)}</FormPill>
        <FormPill>Validated {fmtDscrX(calc?.validatedDscr)}</FormPill>
        <FormPill>Verified {fmtDscrX(calc?.verifiedDscr)}</FormPill>
        <FormPill>
          Verified rows {verifiedCount}/{size(calc?.sources)}
        </FormPill>
        <FormPill>Floor {fmtDscrX(calc?.programFloor)}</FormPill>
      </Flx>
      {calc?.clearsFloor === false ? (
        <FormWarn>
          <strong>
            Verified DSCR {fmtDscrX(calc?.verifiedDscr)} is below the program
            floor {fmtDscrX(calc?.programFloor)}.
          </strong>{" "}
          The lock request is blocked. Lower the loan amount or try a lower
          rate, then verify or correct rows in the DSCR Calculations tab.
        </FormWarn>
      ) : calc?.fullyVerified === false ? (
        <FormWarn>
          <strong>Not every workbook row is verified.</strong> Unverified rows
          are left out of the verified DSCR until UW review, so the lock records{" "}
          {fmtDscrX(calc?.verifiedDscr)} as it stands now.
        </FormWarn>
      ) : isNil(calc?.programFloor) ? (
        <FormNote>
          No program floor is set on the DSCR workbook, so this request isn't
          floor-checked. Set it in the DSCR Calculations tab (1.00 guideline /
          0.80 exception).
        </FormNote>
      ) : null}
    </Flx>
  );
};

DscrWorkbookGate.propTypes = { calc: PropTypes.object };

/* ───────────────────────────── sizer ───────────────────────────────────── */

/**
 * The sizer, one value: `{ files: [uploadEntry] }` for a new upload or
 * `{ docId }` for a document already on the loan. Picking one clears the other.
 */
const SizerPicker = ({ value, onChange, docOptions }) => {
  const files = value?.files || [];
  return (
    <Flx column sx={{ gap: px(FORM.s.s5) }}>
      <FormFileDrop
        label="Upload the sizer"
        value={files}
        onChange={(next) => onChange(next?.length ? { files: next } : null)}
        accept=".pdf,.xls,.xlsx,.csv"
        docGroup={SIZER_DOC_GROUP}
        docType={SIZER_DOC_TYPE}
        hint="PDF, XLS, XLSX or CSV"
      />
      {!files.length && docOptions.length ? (
        <FormSelectField
          label="Or pick one already on the loan"
          value={value?.docId ?? null}
          onChange={(docId) => onChange(docId ? { docId } : null)}
          options={docOptions}
          placeholder="Select a sizer document"
        />
      ) : null}
      {isFilled(value) ? null : (
        <FormWarn>
          <strong>No sizer attached.</strong> The lock can&apos;t be requested
          without one. Upload the sizer or pick one already on the loan.
        </FormWarn>
      )}
    </Flx>
  );
};

SizerPicker.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  docOptions: PropTypes.array,
};

/* ───────────────────────────── sections ────────────────────────────────── */

const SECTIONS = ({
  mode,
  knownProduct,
  maxLockDays,
  workbookRules,
  dscrCalc,
  docOptions,
}) => {
  const priceFields = [
    {
      name: "product",
      label: "Lock product",
      kind: "chips",
      span: 6,
      options: PRODUCT_OPTIONS,
      visibleWhen: () => !knownProduct,
    },
    // RTL — buy rate + spread
    ...[
      ["coupon_rate", "Coupon rate"],
      ["pricing_base_rate", "Pricing base rate"],
      ["pricing_spread", "Pricing spread"],
      ["risk_spread_adjustment", "Risk spread adj."],
    ].map(([name, label]) => ({
      name,
      label,
      kind: "percent",
      span: 2,
      // The requested coupon is the one RTL input the server can't resolve on
      // its own; base rate, spread and class all resolve at lock time.
      req: name === "coupon_rate",
      visibleWhen: (c) => c.values.product === "rtl",
    })),
    {
      name: "borrower_classification_calculated",
      label: "Borrower classification",
      kind: "select",
      span: 2,
      options: RTL_BORROWER_CLASSIFICATIONS,
      placeholder: "Select classification",
      visibleWhen: (c) => c.values.product === "rtl",
    },
    {
      name: "override_buy_rate",
      label: "Override buy rate",
      kind: "percent",
      span: 2,
      optional: true,
      visibleWhen: (c) => c.values.product === "rtl",
    },
    // DSCR — rate + DSCR; margin tier / base price resolve server-side
    {
      name: "rate_at_lock",
      label: "Rate at lock",
      kind: "percent",
      span: 2,
      req: true,
      visibleWhen: (c) => c.values.product !== "rtl",
    },
    {
      name: "calculated_dscr_at_lock",
      label: "Calculated DSCR",
      kind: "number",
      span: 2,
      req: true,
      // With a saved workbook the number is the workbook's, shown in `extra`.
      visibleWhen: (c) => c.values.product !== "rtl" && !workbookRules,
    },
    {
      name: "pricing_version_date",
      label: "Pricing version date",
      kind: "date",
      span: 2,
      helperText:
        "Base price and margin tier resolve from the loan's correspondent at lock time.",
      visibleWhen: (c) => c.values.product !== "rtl",
    },
  ];

  return [
    {
      key: "price",
      title: "Price inputs",
      helper:
        "What the desk prices the lock from. Rates are entered as percents.",
      fields: priceFields,
      extra: ({ ctx }) =>
        ctx.values.product !== "rtl" && workbookRules ? (
          <DscrWorkbookGate calc={dscrCalc} />
        ) : null,
    },
    ...(mode === "edit"
      ? []
      : [
          {
            key: "sizer",
            title: "Sizer",
            helper:
              "Required to request a lock. It is stored with the lock (and parsed for DSCR).",
            fields: [
              {
                name: "sizer",
                label: "Sizer",
                req: true,
                span: 6,
                summary: (v) =>
                  v?.files?.[0]?.file_display_name ||
                  docOptions.find((o) => o.value === v?.docId)?.label ||
                  "Attached",
                render: ({ value, onChange }) => (
                  <SizerPicker
                    value={value}
                    onChange={onChange}
                    docOptions={docOptions}
                  />
                ),
              },
            ],
          },
        ]),
    {
      key: "options",
      title: "Lock options",
      helper: "How long, and through which channel.",
      fields: [
        {
          name: "lock_period_days",
          label: "Requested period",
          kind: "chips",
          span: 6,
          options: REQUEST_LOCK_PERIODS.filter(
            (d) => isNil(maxLockDays) || d <= maxLockDays,
          ).map((d) => ({ value: d, label: `${d} days` })),
          helperText:
            "Over 30 days needs a pricing exception. The desk sets the final expiration.",
          summary: (v) => `${v} days`,
        },
        {
          name: "lock_channel",
          label: "Lock type",
          kind: "chips",
          span: 6,
          options: LOCK_CHANNEL_OPTIONS,
        },
        {
          name: "lock_notes",
          label: "Notes",
          kind: "textarea",
          span: 6,
          optional: true,
          placeholder: "Anything the desk should know",
        },
      ],
    },
  ];
};

/* ───────────────────────────── payload ─────────────────────────────────── */

const RTL_KEYS = [
  "coupon_rate",
  "pricing_base_rate",
  "pricing_spread",
  "risk_spread_adjustment",
  "borrower_classification_calculated",
  "override_buy_rate",
];

/** The price payload the old form posted, nulls dropped. No sizer keys. */
const buildPricePayload = (v, { workbookRules, dscrCalc }) => {
  const p = {
    lock_period_days: isNil(v.lock_period_days)
      ? null
      : Number(v.lock_period_days),
    // lock_channel only — the server derives is_correspondent_lock from it.
    lock_channel: v.lock_channel,
    lock_notes: v.lock_notes || null,
  };
  if (v.product === "rtl") {
    RTL_KEYS.forEach((k) => {
      p[k] = v[k] ?? null;
    });
  } else {
    p.rate_at_lock = v.rate_at_lock ?? null;
    // With a saved workbook the server overwrites this with verifiedDscr
    // regardless; send the same number so the request reads true.
    p.calculated_dscr_at_lock = workbookRules
      ? (dscrCalc?.verifiedDscr ?? null)
      : (v.calculated_dscr_at_lock ?? null);
    p.pricing_version_date = v.pricing_version_date ?? null;
  }
  Object.keys(p).forEach((k) => p[k] == null && delete p[k]);
  return p;
};

const initialValuesOf = (lock, loan, knownProduct) => ({
  product: knownProduct || "dscr",
  rate_at_lock: lock?.rate_at_lock ?? null,
  calculated_dscr_at_lock: lock?.calculated_dscr_at_lock ?? null,
  pricing_version_date: lock?.pricing_version_date ?? null,
  coupon_rate: lock?.coupon_rate ?? null,
  pricing_base_rate: lock?.pricing_base_rate ?? null,
  pricing_spread: lock?.pricing_spread ?? null,
  risk_spread_adjustment: lock?.risk_spread_adjustment ?? null,
  override_buy_rate: lock?.override_buy_rate ?? null,
  borrower_classification_calculated:
    lock?.borrower_classification_calculated ?? null,
  lock_period_days: lock?.lock_period_days ?? DEFAULT_REQUEST_LOCK_PERIOD,
  // Lock type = the deal's channel (what omitting the field would have saved);
  // a re-request keeps whatever the prior lock carried.
  lock_channel: lockChannelOf(lock) ?? defaultLockChannelForDeal(loan),
  lock_notes: lock?.lock_notes ?? "",
  sizer: lock?.loan_sizer?._id ? { docId: lock.loan_sizer._id } : null,
});

/* ───────────────────────────── the form ────────────────────────────────── */

const InlineFrame = ({ body, footer }) => (
  <Box>
    {body}
    <Box sx={{ mt: px(FORM.s.s6) }}>{footer}</Box>
  </Box>
);

const LockRequestForm = ({
  loanDrilldown,
  initialLock,
  mode = "request",
  loading,
  onSubmit,
  onCancel,
  pricingNote,
  maxLockDays,
  frame,
}) => {
  const isEdit = mode === "edit";
  const dealProduct = getLockProduct(loanDrilldown?.loanProductType); // 'dscr' | 'rtl' | null
  const knownProduct = dealProduct || initialLock?.product || null;

  const initialValues = useMemo(
    () => initialValuesOf(initialLock, loanDrilldown, knownProduct),
    // Seed once per lock; the form owns its values after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialLock?._id, knownProduct],
  );
  const [values, setValues] = useState(initialValues);
  const setValue = useCallback((name, v) => {
    if (NEVER_EMPTY.includes(name) && isNil(v)) return;
    setValues((prev) => ({ ...prev, [name]: v }));
  }, []);

  const isRtl = values.product === "rtl";
  const dscrCalc = useDscrWorkbook(loanDrilldown?._id, !isRtl);
  const workbookRules = !isRtl && workbookGoverns(dscrCalc);
  const workbookBlocks = workbookRules && dscrCalc?.clearsFloor === false;

  // Existing loan documents to pick a sizer from — Sizer-typed ones first. A
  // resubmit keeps the prior lock's sizer selectable even if the loan's list
  // doesn't carry it.
  const docOptions = useMemo(() => {
    const docs = Array.isArray(loanDrilldown?.loanDocuments)
      ? loanDrilldown.loanDocuments
      : [];
    const ordered = [
      ...docs.filter(isSizerDoc),
      ...docs.filter((d) => !isSizerDoc(d)),
    ];
    const opts = ordered
      .filter((d) => docRefOf(d))
      .map((d) => ({ value: docRefOf(d), label: docLabelOf(d) }));
    const prior = initialLock?.loan_sizer;
    if (prior?._id && !opts.some((o) => o.value === prior._id)) {
      opts.unshift({ value: prior._id, label: docLabelOf(prior) });
    }
    return opts;
  }, [loanDrilldown?.loanDocuments, initialLock?.loan_sizer]);

  const sections = useMemo(
    () =>
      SECTIONS({
        mode,
        knownProduct,
        maxLockDays,
        workbookRules,
        dscrCalc,
        docOptions,
      }),
    [mode, knownProduct, maxLockDays, workbookRules, dscrCalc, docOptions],
  );
  const ctx = useMemo(() => ({ values }), [values]);
  const required = useMemo(() => requiredOf(sections, ctx), [sections, ctx]);
  const requiredFilled = countRequiredFilled(required, values);

  const payloadOpts = { workbookRules, dscrCalc };
  const pricePayload = buildPricePayload(values, payloadOpts);
  // An edit posts only what changed against the lock it opened on.
  const editPayload = isEdit
    ? (() => {
        const before = buildPricePayload(initialValues, payloadOpts);
        return Object.fromEntries(
          Object.entries(pricePayload).filter(
            ([k, v]) => JSON.stringify(v) !== JSON.stringify(before[k]),
          ),
        );
      })()
    : null;
  const hasChanges = !isEdit || Object.keys(editPayload).length > 0;

  const missingSizer = !isEdit && !isFilled(values.sizer);
  const canSubmit =
    requiredFilled === required.length && hasChanges && !workbookBlocks;
  // Why the primary is off, said on the button itself — the footer only counts.
  const blockedReason = workbookBlocks
    ? "Verified DSCR is below the program floor"
    : missingSizer
      ? "Attach a sizer to request this lock"
      : requiredFilled < required.length
        ? "Fill in the required price inputs"
        : !hasChanges
        ? "No changes to save yet"
        : null;

  const submit = () => {
    if (!canSubmit) return;
    if (isEdit) {
      onSubmit(values.product, editPayload);
      return;
    }
    const payload = { ...pricePayload };
    const file = values.sizer?.files?.[0]?.file;
    if (file) {
      payload.uploadMetadata = [
        {
          docGroup: SIZER_DOC_GROUP,
          docType: SIZER_DOC_TYPE,
          // process_files_with_metadata matches each upload to its metadata by
          // `id` === the file name; any other key leaves the doc untyped.
          id: file.name,
        },
      ];
    } else if (values.sizer?.docId) {
      payload.sizerDocumentId = values.sizer.docId;
    }
    onSubmit(values.product, payload, file ? [file] : undefined);
  };

  const body = (
    <Box>
      <Flx column sx={{ gap: px(FORM.s.s4), mb: px(FORM.s.s6) }}>
        <FormInfo>{HINTS[mode]}</FormInfo>
        {pricingNote}
      </Flx>
      <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
    </Box>
  );

  const primary = (
    <Button
      sx={formSx.button}
      disabled={!canSubmit || loading}
      onClick={submit}
    >
      {loading ? "Sending…" : SUBMIT_TEXT[mode]}
    </Button>
  );

  const footer = (
    <FormPanelFooter
      required={required.length}
      filled={requiredFilled}
      readyText={
        workbookBlocks
          ? "Below the DSCR floor"
          : isEdit
            ? hasChanges
              ? "Ready to save"
              : "No changes yet"
            : "Ready to send"
      }
    >
      <Button
        variant="outlined"
        color="gray"
        sx={formSx.button}
        onClick={onCancel}
      >
        Cancel
      </Button>
      {blockedReason && !loading ? (
        <Tooltip title={blockedReason} placement="top" arrow>
          {/* A disabled button fires no events — the span carries the hover. */}
          <span>{primary}</span>
        </Tooltip>
      ) : (
        primary
      )}
    </FormPanelFooter>
  );

  return frame ? (
    frame({ variant: "panel", view: mode, body, footer })
  ) : (
    <InlineFrame body={body} footer={footer} />
  );
};

LockRequestForm.propTypes = {
  loanDrilldown: PropTypes.object,
  /** The lock an update / edit opens on; absent for a new request. */
  initialLock: PropTypes.object,
  mode: PropTypes.oneOf(["request", "update", "edit"]),
  loading: PropTypes.bool,
  /** (product, payload, files?) */
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  /** Read-only correspondent pricing context, shown above the sections. */
  pricingNote: PropTypes.node,
  maxLockDays: PropTypes.number,
  /** ({ variant, view, body, footer }) → node. Absent → stacked inline. */
  frame: PropTypes.func,
};

export default LockRequestForm;
